import React, { useEffect, useMemo, useState } from 'react'
import { useChartDateBarContext } from '../../context/chart-date-bar-provider'
import ChartDateBar from './ChartDateBar'
import { useDebouncedCallback } from 'use-debounce'
import StickyContentBar from 'src/Libs/header/components/StickyContentBar'

interface Props {
  minFrom?: string /** earliest timestamp which can be selected from which on to display data */
  hasForecastOption?: boolean
}

/**
 * Wraps a chart date bar which allows to configure which data should be displayed.
 * - sticky: sticks to the top of the screen when scrolling
 * - multiple charts: date settings are stored via context and thus can be applied to multiple charts
 */
const StickyMultiChartDateBar: React.FC<Props> = ({ minFrom, hasForecastOption = false }) => {
  const { from, maxFrom, to, maxTo, view, setFrom, setTo, setView, hasCustomRange, setHasCustomRange, isForecastEnabled, setIsForecastEnabled } =
    useChartDateBarContext()

  const [localView, setLocalView] = useState(view)
  const [localFrom, setLocalFrom] = useState(from)
  const [localTo, setLocalTo] = useState(to)
  const memoizedLocalView = useMemo(() => localView, [localView])
  const memoizedLocalFrom = useMemo(() => localFrom, [localFrom])
  const memoizedHasCustomRange = useMemo(() => hasCustomRange, [hasCustomRange])

  const debouncedSync = useDebouncedCallback(() => {
    setView(localView)
    setFrom(localFrom)
    setTo(localTo)
  }, 200)

  useEffect(() => {
    debouncedSync.callback()
  }, [localView, localFrom, localTo, debouncedSync])

  return (
    <StickyContentBar>
      <ChartDateBar
        view={memoizedLocalView}
        setView={setLocalView}
        to={localTo}
        setTo={setLocalTo}
        hasForecastOption={hasForecastOption}
        isForecastEnabled={isForecastEnabled}
        setIsForecastEnabled={setIsForecastEnabled}
        minFrom={minFrom}
        maxFrom={maxFrom}
        maxTo={maxTo}
        from={memoizedLocalFrom}
        setFrom={setLocalFrom}
        customRange={memoizedHasCustomRange}
        setCustomRange={setHasCustomRange}
      />
    </StickyContentBar>
  )
}

export default StickyMultiChartDateBar
